import {
  Button,
  Input,
  NativeSelectField,
  NativeSelectRoot,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useMutation } from "@tanstack/react-query"
import { useState } from "react"
import { FiUpload } from "react-icons/fi"

import type { QuizSectionPublic } from "@/client"
import { OpenAPI } from "@/client"
import { BulkUploadErrorModal } from "@/components/Questions/BulkUploadErrorModal"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import useCustomToast from "@/hooks/useCustomToast"

export interface BatchUploadModalProps {
  isOpen: boolean
  onClose: () => void
  quizId: string
  sections: QuizSectionPublic[]
  onUploadSuccess: () => void
}

type BatchUploadResponse = {
  questions_created: number
  questions_linked: number
  section_id: string | null
  errors: Array<{ row: number; error: string }>
}

async function batchUploadQuestionsToQuiz({
  quizId,
  file,
}: {
  quizId: string
  file: File
}): Promise<BatchUploadResponse> {
  const formData = new FormData()
  formData.append("file", file)

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("access_token") ?? ""
      : ""

  const response = await fetch(
    `${OpenAPI.BASE}/api/v1/quizzes/${quizId}/questions/batch`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    },
  )

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.detail || "Upload failed")
  }

  return response.json()
}

async function batchUploadQuestionsToSection({
  quizId,
  sectionId,
  file,
}: {
  quizId: string
  sectionId: string
  file: File
}): Promise<BatchUploadResponse> {
  const formData = new FormData()
  formData.append("file", file)

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("access_token") ?? ""
      : ""

  const response = await fetch(
    `${OpenAPI.BASE}/api/v1/quizzes/${quizId}/sections/${sectionId}/questions/batch`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    },
  )

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.detail || "Upload failed")
  }

  return response.json()
}

export function BatchUploadModal({
  isOpen,
  onClose,
  quizId,
  sections,
  onUploadSuccess,
}: BatchUploadModalProps) {
  const { showSuccessToast, showErrorToast } = useCustomToast()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedSectionId, setSelectedSectionId] = useState<string>("")
  const [uploadResult, setUploadResult] = useState<BatchUploadResponse | null>(
    null,
  )
  const [errorModalOpen, setErrorModalOpen] = useState(false)

  const uploadMutation = useMutation<BatchUploadResponse, Error, void>({
    mutationFn: async (): Promise<BatchUploadResponse> => {
      if (!selectedFile) {
        throw new Error("No file selected")
      }

      if (selectedSectionId && selectedSectionId !== "unsectioned") {
        return batchUploadQuestionsToSection({
          quizId,
          sectionId: selectedSectionId,
          file: selectedFile,
        })
      }

      return batchUploadQuestionsToQuiz({
        quizId,
        file: selectedFile,
      })
    },
    onSuccess: (data) => {
      setUploadResult(data)

      const sectionName = selectedSectionId
        ? sections.find((s) => s.id === selectedSectionId)?.name
        : null

      const destination = sectionName ? ` to ${sectionName}` : " to quiz"
      const totalAdded = data.questions_linked

      if (data.errors.length === 0) {
        showSuccessToast(`${totalAdded} questions added${destination}`)
      } else {
        showSuccessToast(
          `${totalAdded} questions added${destination}, ${data.errors.length} errors`,
        )
        setErrorModalOpen(true)
      }

      onUploadSuccess()
    },
    onError: (error: Error) => {
      showErrorToast(error.message)
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setSelectedFile(e.target.files[0])
      setUploadResult(null)
    }
  }

  const handleUpload = () => {
    uploadMutation.mutate(undefined)
  }

  const handleClose = () => {
    setSelectedFile(null)
    setSelectedSectionId("")
    setUploadResult(null)
    onClose()
  }

  const handleDownloadTemplate = () => {
    const csv = [
      [
        "question_text",
        "option_a",
        "option_b",
        "option_c",
        "option_d",
        "correct_answer",
        "rationale",
        "subjects",
        "topics",
      ].join(","),
      [
        '"<p>What is 2+2?</p>"',
        "3",
        "4",
        "5",
        "6",
        "b",
        '"<p>2+2 is 4</p>"',
        "Math",
        '"Arithmetic, Basics"',
      ].join(","),
    ].join("\n")

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "quiz-questions-upload-template.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  if (!isOpen) {
    return null
  }

  return (
    <>
      <DialogRoot open={isOpen} onOpenChange={() => handleClose()} size="lg">
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Batch Upload Questions</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />

          <DialogBody>
            <Stack gap={4}>
              <Text fontSize="sm" color="fg.muted">
                Upload a CSV with columns: <b>question_text</b>, <b>option_a</b>
                , <b>option_b</b>, ... (at least two contiguous option columns),{" "}
                <b>correct_answer</b> (matching option letter), <b>rationale</b>
                , <b>subjects</b> (comma-separated), <b>topics</b>{" "}
                (comma-separated).
              </Text>
              <Button
                alignSelf="flex-start"
                size="sm"
                variant="subtle"
                onClick={handleDownloadTemplate}
              >
                Download sample CSV
              </Button>
              <Field label="Select CSV File">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  aria-label="Select CSV file"
                />
              </Field>

              <Field label="Section (optional)">
                <NativeSelectRoot>
                  <NativeSelectField
                    value={selectedSectionId}
                    onChange={(e) => setSelectedSectionId(e.target.value)}
                    aria-label="Section (optional)"
                  >
                    <option value="">Unsectioned</option>
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.name}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>

              {uploadResult && uploadResult.questions_linked > 0 && (
                <Text color="green.500" fontWeight="bold">
                  {uploadResult.questions_linked} questions added
                  {selectedSectionId &&
                    ` to ${
                      sections.find((s) => s.id === selectedSectionId)?.name
                    }`}
                </Text>
              )}
            </Stack>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              colorPalette="blue"
              onClick={handleUpload}
              disabled={!selectedFile || uploadMutation.isPending}
              gap={2}
            >
              {!uploadMutation.isPending && <FiUpload />}
              {uploadMutation.isPending ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
      <BulkUploadErrorModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        errors={uploadResult?.errors ?? []}
      />
    </>
  )
}
